import React, { useState } from "react";

import type { SolverPhaseNeedsHeaderDayItem } from "@/components/sections/SolverPhaseNeeds.types.tsx";

export type RoleDayWeightRow = {
    name: string;
    id: string;
};

export type RoleDayWeightGridProps = {
    headerDays: SolverPhaseNeedsHeaderDayItem[];
    rows: RoleDayWeightRow[];
    weights: Record<string, Record<string, number>>;
    defaultWeight: number;
    onWeightChange: (roleId: string, dayId: string, value: number) => void;
};

const DAY_COL_WIDTH = "5rem";
// The solver clamps to this cap, so older values above it are shown as applied.
const MAX_WEIGHT = 1000;

type WeightInputProps = {
    label: string;
    value: number;
    defaultWeight: number;
    onChange: (value: number) => void;
};

const WeightInput: React.FC<WeightInputProps> = ({ label, value, defaultWeight, onChange }) => {
    // Typed text is kept while focused: an emptied field must not snap back to a number mid-edit.
    const [draft, setDraft] = useState<string | null>(null);

    return (
        <input
            type="number"
            min="0"
            max={MAX_WEIGHT}
            aria-label={label}
            value={draft ?? Math.min(MAX_WEIGHT, value)}
            onChange={(e) => {
                const raw = e.target.value;
                const parsed = Math.round(Number(raw));

                setDraft(raw);
                if (raw !== "" && Number.isFinite(parsed)) {
                    onChange(Math.min(MAX_WEIGHT, Math.max(0, parsed)));
                }
            }}
            onBlur={() => {
                if (draft === "") {
                    onChange(defaultWeight);
                }
                setDraft(null);
            }}
            className="w-14 py-1 text-center border border-gray-400 rounded focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
    );
};

const RoleDayWeightGrid: React.FC<RoleDayWeightGridProps> = (props) => {
    return (
        <table className="table-auto border-collapse border border-gray-300 w-full">
            <thead>
                <tr className="bg-gray-100">
                    <td className="border border-gray-300 px-4 py-2">{"Rôle"}</td>
                    {props.headerDays.map((day) => (
                        <td
                            key={day.id}
                            className="border border-gray-300 px-2 py-2 text-center"
                            style={{ width: DAY_COL_WIDTH }}
                        >
                            {day.label}
                        </td>
                    ))}
                </tr>
            </thead>
            <tbody>
                {props.rows.map((row) => (
                    <tr key={row.id}>
                        <td className="border border-gray-300 px-4 py-2 align-middle">{row.name}</td>
                        {props.headerDays.map((day) => {
                            const value = props.weights[row.id]?.[day.id];

                            return (
                                <td
                                    key={day.id}
                                    className="border border-gray-300 px-2 py-1 align-middle text-center"
                                    style={{ width: DAY_COL_WIDTH }}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        <WeightInput
                                            label={`${row.name} — ${day.label}`}
                                            value={value ?? props.defaultWeight}
                                            defaultWeight={props.defaultWeight}
                                            onChange={(weight) => props.onWeightChange(row.id, day.id, weight)}
                                        />
                                        <span className="text-xs text-gray-500">%</span>
                                    </span>
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default RoleDayWeightGrid;
